import type {
  DateTimeFormatter,
  FormatterWithPrecision,
} from '../../../../core/types/formatter.js';
import { formatDateBasedOnDomain } from '../../../../core/utils/format-date-based-on-domain.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type {
  AnyXYChartScale,
  AxisIdToFormatter,
  XYChartAxisInternal,
} from '../../../types/xy-chart-internals.js';

interface AxisFormatterConfig {
  axisConfig:
    | Pick<XYChartAxisInternal, 'id' | 'formatter' | 'type'>
    | undefined;
  axisId: string;
  scale?: AnyXYChartScale;
  axesIdToFormatter: AxisIdToFormatter;
  width: number;
}

/**
 * Returns a formatter function for the axis, handling time axes with no custom formatter.
 */
export function getAxisFormatterWithTimeFallback({
  axisConfig,
  axisId,
  scale,
  axesIdToFormatter,
  width,
}: AxisFormatterConfig): FormatterWithPrecision | DateTimeFormatter {
  if (!axisConfig) {
    return safeGet(axesIdToFormatter, axisId);
  }

  const { formatter, type } = axisConfig;

  if (type === 'time' && !formatter) {
    return (value: number | Date) => {
      const dateValue = value instanceof Date ? value : new Date(value);
      return formatDateBasedOnDomain(
        dateValue,
        scale?.domain() as [Date, Date],
        width,
      );
    };
  } else {
    return safeGet(axesIdToFormatter, axisId);
  }
}
